import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBackOutlined,
  ContentCopyOutlined,
  InfoOutlined,
  KeyOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  exchangeIfoodAuthorization,
  getIfoodImportStatus,
  listIfoodMerchants,
  selectIfoodMerchant,
  startIfoodAuthorization,
} from '../../services/api';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { PRODUCT_IMAGES } from '../../branding/assets';
import { getIfoodErrorMessage } from '../../utils/ifoodError';
import IfoodLoadingBackdrop from '../common/IfoodLoadingBackdrop';

const IFOOD_RED = '#EA1D2C';

const IfoodLogo = ({ size = 24 }) => (
  <Box component="svg" role="img" aria-label="iFood" viewBox="0 0 24 24" sx={{ width: size, height: size }}>
    <path
      fill="currentColor"
      d="M8.428 1.67c-4.65 0-7.184 4.149-7.184 6.998 0 2.294 2.2 3.299 4.25 3.299l-.006-.006c4.244 0 7.184-3.854 7.184-6.998 0-2.29-2.175-3.293-4.244-3.293zm11.328 0c-4.65 0-7.184 4.149-7.184 6.998 0 2.294 2.2 3.299 4.25 3.299l-.006-.006C21.061 11.96 24 8.107 24 4.963c0-2.29-2.18-3.293-4.244-3.293zM14.172 14.52l2.435 1.834c-2.17 2.07-6.124 3.525-9.353 3.17A8.913 8.913 0 01.23 14.541H0a9.598 9.598 0 008.828 7.758c3.814.24 7.323-.905 9.947-3.13l-.004.007 1.08 2.988 1.555-7.623-7.234-.02Z"
    />
  </Box>
);

const StepBadge = ({ children, active = true }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      fontSize: 13,
      fontWeight: 900,
      color: active ? '#fff' : '#64748b',
      backgroundColor: active ? IFOOD_RED : '#e2e8f0',
    }}
  >
    {children}
  </Box>
);

const getMerchantName = (merchant) => (
  merchant?.name || merchant?.corporateName || merchant?.corporate_name || 'Loja iFood'
);

const PRODUCT_LINKS = [
  {
    key: 'atendabot',
    label: 'AtendaBot',
    path: '/atendabot/prompt',
    logo: PRODUCT_IMAGES.atendabot,
    color: '#282569',
    borderColor: '#b8b4e8',
    background: 'linear-gradient(135deg, #f3f1ff 0%, #e8fbf4 100%)',
    hoverBackground: 'linear-gradient(135deg, #e9e6ff 0%, #d9f7eb 100%)',
    logoBackground: '#3127a0',
  },
  {
    key: 'cardapio',
    label: 'One Menu',
    path: '/cardapio',
    logo: PRODUCT_IMAGES.oneMenu,
    color: '#1f4fbf',
    borderColor: '#9db8f5',
    background: 'linear-gradient(135deg, #eff5ff 0%, #fff3ec 100%)',
    hoverBackground: 'linear-gradient(135deg, #e4edff 0%, #ffe8dc 100%)',
    logoBackground: '#fff8f3',
  },
  {
    key: 'feedback_hero',
    label: 'FeedbackHero',
    path: '/feedback-hero',
    logo: PRODUCT_IMAGES.feedbackHero,
    color: '#1f5c3a',
    borderColor: '#86caa5',
    background: 'linear-gradient(135deg, #ecf8f1 0%, #e7f5ed 100%)',
    hoverBackground: 'linear-gradient(135deg, #ddf2e6 0%, #d8eee2 100%)',
    logoBackground: '#0b3327',
  },
];

const IfoodConnectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasProductAccess } = useAuthSession();
  const [status, setStatus] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [expandedHelp, setExpandedHelp] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const callbackExchangeRef = useRef('');

  const callback = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      code: params.get('authorizationCode') || params.get('authorization_code') || params.get('code') || '',
      merchantId: params.get('merchantId') || params.get('merchant_id') || '',
    };
  }, [location.search]);

  const clearCallback = useCallback(() => {
    const params = new URLSearchParams(location.search);
    ['authorizationCode', 'authorization_code', 'code', 'merchantId', 'merchant_id']
      .forEach((key) => params.delete(key));
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const loadAllMerchants = useCallback(async () => {
    const rows = [];
    for (let page = 1; page <= 20; page += 1) {
      const response = await listIfoodMerchants({ page, size: 100 });
      const nextRows = Array.isArray(response?.merchants) ? response.merchants : [];
      rows.push(...nextRows);
      if (!response?.pagination?.has_next || nextRows.length === 0) break;
    }
    setMerchants(rows);
    return rows;
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const nextStatus = await getIfoodImportStatus();
      setStatus(nextStatus);
      setAuthData(nextStatus?.user_code ? {
        user_code: nextStatus.user_code,
        verification_url: nextStatus.verification_url,
        verification_url_complete: nextStatus.verification_url_complete,
        expires_at: nextStatus.code_expires_at,
      } : null);
      if (nextStatus?.connected) await loadAllMerchants();
      else setMerchants([]);
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível consultar a conexão com o iFood.'));
    } finally {
      setLoading(false);
    }
  }, [loadAllMerchants]);

  useEffect(() => {
    if (!callback.code) load();
  }, [callback.code, load]);

  const completeAuthorization = useCallback(async (rawCode, { clearUrl = false } = {}) => {
    const code = String(rawCode || '').trim();
    if (!code) {
      toast.error('Cole o código de autorização exibido pelo iFood.');
      return false;
    }

    try {
      setBusy('exchange');
      const nextStatus = await exchangeIfoodAuthorization(code);
      setStatus(nextStatus);
      setAuthData(null);
      setAuthorizationCode('');
      const returned = Array.isArray(nextStatus?.merchants) ? nextStatus.merchants : [];
      setMerchants(returned);
      if (!returned.length) await loadAllMerchants();
      toast.success('iFood conectado com sucesso.');
      return true;
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível concluir a conexão com o iFood.'));
      return false;
    } finally {
      setBusy('');
      if (clearUrl) clearCallback();
    }
  }, [clearCallback, loadAllMerchants]);

  useEffect(() => {
    const code = String(callback.code || '').trim();
    if (!code) return;
    const callbackKey = `${code}:${callback.merchantId}`;
    if (callbackExchangeRef.current === callbackKey) return;
    callbackExchangeRef.current = callbackKey;

    completeAuthorization(code, { clearUrl: true });
  }, [callback.code, callback.merchantId, completeAuthorization]);

  const handleCompleteAuthorization = async () => {
    await completeAuthorization(authorizationCode);
  };

  const handleConnect = async () => {
    const partnerWindow = window.open('', '_blank');
    try {
      setBusy('start');
      const response = await startIfoodAuthorization();
      setStatus(response);
      setAuthData(response?.authorization || null);
      const url = response?.authorization?.verification_url_complete
        || response?.authorization?.verification_url;
      if (!url) throw new Error('O iFood não informou o endereço para autorização.');
      if (partnerWindow) {
        partnerWindow.opener = null;
        partnerWindow.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (error) {
      partnerWindow?.close();
      toast.error(getIfoodErrorMessage(error, 'Não foi possível abrir a conexão com o iFood.'));
    } finally {
      setBusy('');
    }
  };

  const handleCopyUserCode = async () => {
    const code = String(authData?.user_code || '').trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Código de ativação copiado.');
    } catch {
      toast.info(`Copie este código: ${code}`);
    }
  };

  const openProduct = async (merchant, product) => {
    try {
      setBusy(`merchant:${merchant.id}`);
      await selectIfoodMerchant(merchant.id);
      const params = new URLSearchParams({ ifoodMerchantId: merchant.id });
      navigate(`${product.path}?${params.toString()}`);
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível abrir esta loja.'));
    } finally {
      setBusy('');
    }
  };

  const connected = Boolean(status?.connected);
  const toggleHelp = (key) => setExpandedHelp((current) => (current === key ? '' : key));

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc', py: { xs: 3, md: 5 } }}>
      <IfoodLoadingBackdrop
        open={loading || Boolean(busy)}
        message={busy === 'exchange' ? 'Concluindo a conexão com o iFood…' : 'Aguardando o iFood…'}
      />
      <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Tooltip title="Voltar para Minha conta">
          <IconButton
            onClick={() => navigate('/conta')}
            aria-label="Voltar para Minha conta"
            sx={{ mb: 2, border: '1px solid #e2e8f0', backgroundColor: '#fff' }}
          >
            <ArrowBackOutlined />
          </IconButton>
        </Tooltip>

        <Card sx={{ mb: 3, borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 16px 40px -24px rgba(15,23,42,.3)' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 52, height: 52, borderRadius: '16px', display: 'grid', placeItems: 'center', color: '#fff', backgroundColor: IFOOD_RED, flexShrink: 0 }}>
                <IfoodLogo size={30} />
              </Box>
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h5" fontWeight={850} color="#0f172a">Integração iFood</Typography>
                  <Tooltip title="Conecte sua conta para usar as lojas do iFood no AtendaBot, One Menu e FeedbackHero." arrow>
                    <IconButton size="small" onClick={() => toggleHelp('header')} aria-label="Saiba mais sobre a integração iFood">
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Collapse in={expandedHelp === 'header'}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Conecte sua conta para usar as lojas do iFood no AtendaBot, One Menu e FeedbackHero.
                  </Typography>
                </Collapse>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {!loading && !connected && (
          <Card sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 16px 40px -24px rgba(15,23,42,.3)' }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h6" fontWeight={800} color="#0f172a">Conectar conta</Typography>
                <Tooltip title="Abra o Portal do Parceiro, autorize o acesso e volte para concluir a conexão caso o iFood forneça um código." arrow>
                  <IconButton size="small" onClick={() => toggleHelp('connection')} aria-label="Como conectar a conta do iFood">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Collapse in={expandedHelp === 'connection'}>
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: '12px' }}>
                  Abra o Portal do Parceiro, autorize o acesso e volte para concluir a conexão caso o iFood forneça um código. Sua senha do iFood não será solicitada aqui.
                </Alert>
              </Collapse>

              <Stack spacing={2.5} sx={{ mt: 3 }} divider={<Divider flexItem />}>
                <Box>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <StepBadge>1</StepBadge>
                    <Typography fontWeight={800}>Abra o Portal do iFood</Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    disabled={Boolean(busy)}
                    startIcon={<IfoodLogo size={22} />}
                    sx={{ borderRadius: '12px', px: 2.5, minHeight: 48, textTransform: 'none', fontWeight: 800, backgroundColor: IFOOD_RED, whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#c91524' } }}
                  >
                    {authData ? 'Gerar novo código' : 'Abrir Portal do iFood'}
                  </Button>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <StepBadge active={Boolean(authData)}>2</StepBadge>
                    <Typography fontWeight={800} color={authData ? 'text.primary' : 'text.secondary'}>
                      Use o código de ativação
                    </Typography>
                  </Stack>
                  {authData?.user_code ? (
                    <>
                      <Alert severity="info" sx={{ borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box>
                              <Typography variant="caption" color="text.secondary">Código de ativação</Typography>
                              <Typography variant="h6" fontWeight={900} sx={{ fontFamily: 'monospace', letterSpacing: '0.12em' }}>{authData.user_code}</Typography>
                            </Box>
                            <Tooltip title="Use este código somente se o Portal do Parceiro pedir um código de ativação." arrow>
                              <IconButton size="small" onClick={() => toggleHelp('activation')} aria-label="Como usar o código de ativação">
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            <Button size="small" startIcon={<ContentCopyOutlined />} onClick={handleCopyUserCode} sx={{ textTransform: 'none', fontWeight: 700 }}>Copiar</Button>
                            {(authData.verification_url_complete || authData.verification_url) && (
                              <Button
                                component="a"
                                href={authData.verification_url_complete || authData.verification_url}
                                target="_blank"
                                rel="noreferrer"
                                size="small"
                                endIcon={<OpenInNewOutlined />}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                              >
                                Reabrir portal
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Alert>
                      <Collapse in={expandedHelp === 'activation'}>
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pt: 1 }}>
                          Use este código somente se o Portal do Parceiro pedir um código de ativação.
                        </Typography>
                      </Collapse>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: { sm: 5 } }}>
                      O código aparecerá aqui depois que você abrir o portal.
                    </Typography>
                  )}
                </Box>

                <Box>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                      <StepBadge active={Boolean(authData)}>3</StepBadge>
                      <Typography fontWeight={800} color={authData ? 'text.primary' : 'text.secondary'}>Finalize a conexão</Typography>
                      <Tooltip title="Cole o código que o iFood mostrar depois que você concluir a autorização no Portal do Parceiro." arrow>
                        <IconButton size="small" onClick={() => toggleHelp('authorization')} aria-label="Onde encontrar o código de autorização">
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Collapse in={expandedHelp === 'authorization'}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Cole o código que o iFood mostrar depois que você concluir a autorização no Portal do Parceiro.
                      </Typography>
                    </Collapse>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
                    <TextField
                      fullWidth
                      label="Código fornecido pelo iFood"
                      value={authorizationCode}
                      onChange={(event) => setAuthorizationCode(event.target.value)}
                      disabled={Boolean(busy) || !authData}
                      placeholder={authData ? 'Cole o código aqui' : 'Disponível após abrir o portal'}
                      InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlined color="action" /></InputAdornment> }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleCompleteAuthorization}
                      disabled={Boolean(busy) || !authData || !authorizationCode.trim()}
                      sx={{ width: { xs: '100%', md: 190 }, height: 56, borderRadius: '12px', textTransform: 'none', fontWeight: 800, backgroundColor: IFOOD_RED, '&:hover': { backgroundColor: '#c91524' } }}
                    >
                      Concluir conexão
                    </Button>
                  </Stack>
                  </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!loading && connected && (
          <Card sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 16px 40px -24px rgba(15,23,42,.3)' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 2.5 }}>
                Lojas do iFood
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Nome da loja</TableCell>
                      <TableCell aria-label="Produtos" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {merchants.map((merchant) => (
                      <TableRow key={merchant.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{getMerchantName(merchant)}</TableCell>
                        <TableCell align="right">
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="flex-end">
                            {PRODUCT_LINKS.map((product) => {
                              const allowed = typeof hasProductAccess !== 'function'
                                || hasProductAccess(product.key, { allowTrialRestricted: true });
                              return (
                                <Tooltip key={product.key} title={allowed ? '' : 'Produto não disponível nesta conta'}>
                                  <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', md: 'auto' } }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={!allowed || Boolean(busy)}
                                      onClick={() => openProduct(merchant, product)}
                                      aria-label={`Abrir ${product.label} para ${getMerchantName(merchant)}`}
                                      sx={{
                                        width: { xs: '100%', md: 154 },
                                        minHeight: 52,
                                        justifyContent: 'flex-start',
                                        gap: 1,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: '12px',
                                        borderColor: product.borderColor,
                                        color: product.color,
                                        background: product.background,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 5px 14px -10px rgba(15, 23, 42, 0.55)',
                                        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                                        '&:hover': {
                                          borderColor: product.color,
                                          background: product.hoverBackground,
                                          boxShadow: '0 8px 18px -10px rgba(15, 23, 42, 0.6)',
                                          transform: 'translateY(-1px)',
                                        },
                                        '&.Mui-disabled': {
                                          borderColor: '#e2e8f0',
                                          color: '#94a3b8',
                                          background: '#f8fafc',
                                        },
                                        '&.Mui-disabled img': {
                                          filter: 'grayscale(1)',
                                          opacity: 0.55,
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 38,
                                          height: 38,
                                          flexShrink: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          overflow: 'hidden',
                                          borderRadius: '9px',
                                          backgroundColor: product.logoBackground,
                                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.45)',
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={product.logo}
                                          alt=""
                                          aria-hidden="true"
                                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                      </Box>
                                      <Typography component="span" variant="body2" fontWeight={800}>
                                        {product.label}
                                      </Typography>
                                    </Button>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!merchants.length && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 5, color: '#64748b' }}>
                          Nenhuma loja foi encontrada para esta conta do iFood.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default IfoodConnectionPage;
